import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAlert } from 'react-alert';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import makeAnimated from 'react-select/animated';

import { getAllCategories } from '../../../actions/commonActions';
import { getQuestionDetail, updateQuestion, clearErrors, getTopics } from '../../../actions/adminQuestionActions';
import { getUniversityList } from '../../../actions/adminUniversityAction';
import { getTopicSections } from '../../../actions/adminTopicSectionActions';
import { UPDATE_QUESTION_RESET } from '../../../constants/adminQuestionConstants';

import Loader from '../../Loader';
import FroalaEditor from 'react-froala-wysiwyg';
import withFroalaLicense from '../../../utils/froalaConfig';
import { questionImageUrl } from '../../../utils/assets';

import './Question.css';

const resolveImagePreview = (value) => {
    if (!value) {
        return '';
    }

    if (typeof value === 'string' && /^data:/i.test(value)) {
        return value;
    }

    if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
        return value;
    }

    return questionImageUrl(value);
};

const buildFroalaConfig = (base = {}) =>
    withFroalaLicense({
        quickInsertTags: [''],
        pasteAllowLocalImages: true,
        imageUpload: true,
        ...base,
    });

const passageToolbar = [
    'undo',
    'redo',
    '|',
    'bold',
    'italic',
    'underline',
    '|',
    'alignLeft',
    'alignCenter',
    'alignRight',
    'alignJustify',
    '|',
    'formatOL',
    'formatUL',
    '|',
    'insertLink',
    'insertImage',
    '|',
    // WIRIS/MathType
    'wirisEditor',
    'wirisChemistry',
    'html',
    'fullscreen',
];

const optionToolbar = [
    'undo',
    'redo',
    '|',
    'bold',
    'italic',
    'underline',
    '|',
    'alignLeft',
    'alignCenter',
    'alignRight',
    'alignJustify',
    '|',
    'formatOL',
    'formatUL',
    '|',
    'insertLink',
    'insertImage',
    '|',
    'wirisEditor',
    'wirisChemistry',
    'html',
];

const EditQuestion = ({ history, match }) => {
    const alert = useAlert();
    const dispatch = useDispatch();
    const animatedComponents = makeAnimated();

    const [questionId, setQuestionId] = useState('');
    const [questionName, setQuestionName] = useState('');
    const [title, setTitle] = useState('');
    const [topic, setTopic] = useState('');
    const [section, setSection] = useState('');
    const [passageCheck, setPassageCheck] = useState(false);
    const [passage, setPassage] = useState('');
    const [option1, setOption1] = useState('');
    const [option2, setOption2] = useState('');
    const [option3, setOption3] = useState('');
    const [option4, setOption4] = useState('');
    const [option5, setOption5] = useState('');
    const [correctAnswer, setCorrectAnswer] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState(false);
    const [trial, setTrial] = useState(false);
    const [logo, setLogo] = useState('');
    const [imageName, setImageName] = useState('');
    const [oldImage, setOldImage] = useState('');
    const [imagePreview, setImagePreview] = useState('');
    const [currentAnswer, setCurrentAnswer] = useState('');
    const [university, setUniversity] = useState([]);
    const [universityLists, setUniversityLists] = useState([]);
    const [errorData, setErrorData] = useState({});

    const { isUpdated, error, errors } = useSelector((state) => state.questions);
    const { question, user_loading: questionLoading } = useSelector((state) => state.questionDetail);
    const { allTopics } = useSelector((state) => state.questions);
    const { topicSections } = useSelector((state) => state.topicSections);
    const { universityList } = useSelector((state) => state.university);

    const subject = localStorage.getItem('subjectName');
    const examSlug = localStorage.getItem('examSlug');
    const subjectId = match.params.subjectId;
    const yearId = match.params.yearId;
    const currentId = match.params.id;
    const topicId = match.params.topicId;
    const slug = examSlug;
    const subjectName = subject;

    const sectionIdFromQuestion = question?.section;
    const topicIdFromQuestion = question?.topic;
    const yearIdFromQuestion = question?.year;
    const subjectIdFromQuestion = question?.subject;

    useEffect(() => {
        dispatch(getUniversityList());
        dispatch(getAllCategories());
        dispatch(getQuestionDetail(subjectId, yearId, currentId));
        dispatch(getTopics(subjectId));
    }, [dispatch, subjectId, yearId, currentId]);

    useEffect(() => {
        if (topic) {
            dispatch(getTopicSections(topic));
        }
    }, [dispatch, topic]);

    useEffect(() => {
        if (errors) {
            setErrorData(errors);
        }

        if (question && Object.keys(question).length > 0) {
            setQuestionId(question._id);
            setQuestionName(question.question);
            setTitle(question.title);
            setPassageCheck(Boolean(question.passageCheck));
            setPassage(question.passage || '');
            setCorrectAnswer(question.correctAns || '');
            setDescription(question.description || '');
            setTopic(question.topic || '');
            setSection(question.section || '');

            const options = question.options || [];
            const optionSetters = [setOption1, setOption2, setOption3, setOption4, setOption5];
            optionSetters.forEach((setter, idx) => {
                const optionItem = options[idx];
                if (optionItem) {
                    setter(optionItem.option);
                    if (question.correctAns === optionItem._id) {
                        setCurrentAnswer(`option_${idx + 1}`);
                    }
                } else {
                    setter('');
                }
            });

            setStatus(Boolean(question.status));
            setTrial(Boolean(question.trial));
            setLogo(question.logo || '');
            setOldImage(question.logo || '');
            setImagePreview(resolveImagePreview(question.logo || ''));
        }

        if (error) {
            alert.error(error);
            dispatch(clearErrors());
        }

        if (isUpdated) {
            alert.success('Question updated successfully');
            history.push(`/admin/questions/${sectionIdFromQuestion}/${subjectIdFromQuestion}/${yearIdFromQuestion}/${topicIdFromQuestion}`);
            dispatch({ type: UPDATE_QUESTION_RESET });
        }
    }, [
        alert,
        dispatch,
        error,
        errors,
        history,
        isUpdated,
        question,
        sectionIdFromQuestion,
        subjectIdFromQuestion,
        yearIdFromQuestion,
        topicIdFromQuestion,
    ]);

    useEffect(() => {
        if (
            examSlug === 'post-utme' &&
            Array.isArray(universityList) &&
            universityList.length > 0
        ) {
            const formatted = universityList.map((item) => ({
                label: item.title,
                value: item._id,
            }));
            setUniversityLists(formatted);

            if (question?.university?.length) {
                const selected = question.university
                    .map((id) => formatted.find((entry) => entry.value === id))
                    .filter(Boolean);
                setUniversity(selected);
            }
        }
    }, [examSlug, question, universityList]);

    const onChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        if (!file.name.match(/\.(jpg|jpeg|png|gif)$/i)) {
            setLogo('');
            setImageName('');
            alert.error('Please select a valid image.');
            setImagePreview('');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const result = event.target?.result;
            if (!result || typeof result !== 'string') {
                return;
            }

            setLogo(result);
            setImageName(file.name);
            setImagePreview(resolveImagePreview(result));
        };
        reader.readAsDataURL(file);
    };

    const handleAnswerOnChange = (e) => {
        setCorrectAnswer(e.target.value);
        setCurrentAnswer(e.target.dataset.ans);
    };

    const submitHandler = (e) => {
        e.preventDefault();
        dispatch(
            updateQuestion(
                questionId,
                title,
                topic,
                section,
                questionName,
                passageCheck,
                passage,
                option1,
                option2,
                option3,
                option4,
                option5,
                correctAnswer,
                currentAnswer,
                description,
                status,
                trial,
                oldImage,
                logo,
                imageName,
            ),
        );
    };

    const optionEditors = useMemo(
        () => [
            { value: option1, setter: setOption1 },
            { value: option2, setter: setOption2 },
            { value: option3, setter: setOption3 },
            { value: option4, setter: setOption4 },
            { value: option5, setter: setOption5 },
        ],
        [option1, option2, option3, option4, option5],
    );

    return (
        <Fragment>
            {questionLoading ? (
                <Loader />
            ) : (
                <Fragment>
                    <div className="content-wrapper">
                        <section className="content-header">
                            <h1>
                                Edit
                                <small>Question</small>
                            </h1>
                            <ol className="breadcrumb">
                                <li>
                                    <Link to="/admin">
                                        <i className="fa fa-dashboard" /> Dashboard
                                    </Link>
                                </li>
                                <li>
                                    <Link to={`/admin/questions/${sectionIdFromQuestion}/${subjectIdFromQuestion}/${yearIdFromQuestion}/${topicIdFromQuestion}`}>
                                        <i className="fa fa-users" /> Questions
                                    </Link>
                                </li>
                                <li className="active">
                                    Edit Question [Exam:- {slug}, Subject:- {subjectName}]
                                </li>
                            </ol>
                        </section>

                        <section className="content">
                            <div className="box box-default">
                                <div className="box-header with-border">
                                    <h3 className="box-title">Edit Question</h3>
                                </div>

                                <form id="quickForm" onSubmit={submitHandler} encType="multipart/form-data">
                                    <div className="box-body">
                                        <div className="form-group row">
                                            <div className="col-md-6">
                                                <label htmlFor="title">Title</label>
                                                <input
                                                    type="text"
                                                    name="title"
                                                    className="form-control"
                                                    id="title"
                                                    placeholder="Enter Title"
                                                    value={title}
                                                    onChange={(e) => setTitle(e.target.value)}
                                                />
                                                {errorData.title ? <span className="text-danger">{errorData.title}</span> : ''}
                                            </div>
                                            <div className="col-md-3">
                                                <label htmlFor="topic_id">Topic</label>
                                                <select className="form-control" name="topic_id" value={topic} onChange={(e) => setTopic(e.target.value)}>
                                                    <option value="">Select</option>
                                                    {allTopics.map((val) => (
                                                        <option key={val._id} value={val._id}>
                                                            {val.title}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errorData.topic_id ? <span className="text-danger">{errorData.topic_id}</span> : ''}
                                            </div>
                                            <div className="col-md-3">
                                                <label htmlFor="topic_section_id">Topic Section</label>
                                                <select className="form-control" name="topic_section_id" value={section} onChange={(e) => setSection(e.target.value)}>
                                                    <option value="">Select</option>
                                                    {topicSections.map((val) => (
                                                        <option key={val._id} value={val._id}>
                                                            {val.section_name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errorData.topic_section_id ? <span className="text-danger">{errorData.topic_section_id}</span> : ''}
                                            </div>
                                        </div>

                                        {examSlug === 'post-utme' && (
                                            <div className="form-group row">
                                                <div className="col-md-12">
                                                    <label htmlFor="title">Select University</label>
                                                    <Select
                                                        value={university}
                                                        onChange={setUniversity}
                                                        options={universityLists}
                                                        isMulti
                                                        components={animatedComponents}
                                                        isSearchable
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <Fragment>
                                            <div className="form-group row">
                                                <div className="col-md-6">
                                                    <label htmlFor="passage_check">
                                                        {subject === 'english' ? 'Common Passage / Linked Question' : 'Linked Question'}
                                                    </label>
                                                    <input
                                                        style={{ marginRight: '5px' }}
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="passage_check"
                                                        name="passage_check"
                                                        checked={passageCheck}
                                                        value={passageCheck}
                                                        onChange={() => setPassageCheck((prev) => !prev)}
                                                    />
                                                </div>
                                            </div>

                                            {passageCheck && (
                                                <div className="form-group row">
                                                    <div className="col-md-12">
                                                        <label htmlFor="Passage_Image">
                                                            {subject !== 'english' ? 'Question Information' : 'Passage / Question Information'}
                                                        </label>
                                                        <FroalaEditor
                                                            key={`${currentId}-passage`}
                                                            tag="textarea"
                                                            model={passage ?? ''}
                                                            onModelChange={setPassage}
                                                            config={buildFroalaConfig({
                                                                height: 200,
                                                                toolbarButtons: passageToolbar,
                                                            })}
                                                        />
                                                        {errorData.passage ? <span className="text-danger">{errorData.passage}</span> : ''}
                                                    </div>
                                                </div>
                                            )}
                                        </Fragment>

                                        <div className="form-group row">
                                            <div className="col-md-12">
                                                <label htmlFor="question">Question</label>
                                                <FroalaEditor
                                                    key={`${currentId}-question`}
                                                    tag="textarea"
                                                    model={questionName ?? ''}
                                                    onModelChange={setQuestionName}
                                                    config={buildFroalaConfig({
                                                        height: 200,
                                                        toolbarButtons: passageToolbar,
                                                    })}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group row">
                                            {question?.options?.map((val, idx) => {
                                                const optionIndex = `option_${idx + 1}`;
                                                const optionData = optionEditors[idx];
                                                if (!optionData) {
                                                    return null;
                                                }

                                                return (
                                                    <div key={val._id || idx} className="col-md-6">
                                                        <label htmlFor={optionIndex}>{`Option #${idx + 1}`}</label>
                                                        <FroalaEditor
                                                            key={`${currentId}-${optionIndex}`}
                                                            tag="textarea"
                                                            model={optionData.value ?? ''}
                                                            onModelChange={optionData.setter}
                                                            config={buildFroalaConfig({
                                                                height: 150,
                                                                toolbarButtons: optionToolbar,
                                                            })}
                                                        />
                                                        <input
                                                            type="checkbox"
                                                            name="correct_answer"
                                                            data-ans={optionIndex}
                                                            value={val._id}
                                                            checked={correctAnswer === val._id}
                                                            onChange={handleAnswerOnChange}
                                                        />
                                                        <label htmlFor={`${optionIndex}_answer`}> &nbsp;Correct Answer</label>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="form-group row">
                                            <div className="col-md-6">
                                                <label htmlFor="logo">Image</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    id="logo"
                                                    name="logo"
                                                    onChange={onChange}
                                                    accept=".jpeg, .jpg, .png"
                                                />
                                                {errorData.logo ? <span className="text-danger">{errorData.logo}</span> : ''}
                                                <div>
                                                    <img id="img-preview" src={imagePreview} alt="Question" />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group row">
                                            <div className="col-md-6">
                                                <label htmlFor="description">Description</label>
                                                <FroalaEditor
                                                    key={`${currentId}-description`}
                                                    tag="textarea"
                                                    model={description ?? ''}
                                                    onModelChange={setDescription}
                                                    config={buildFroalaConfig({
                                                        height: 200,
                                                        toolbarButtons: optionToolbar,
                                                    })}
                                                />
                                                {errorData.description ? <span className="text-danger">{errorData.description}</span> : ''}
                                            </div>
                                        </div>

                                        <div className="form-group row">
                                            <div className="col-md-6">
                                                <label htmlFor="trial">Demo / Trial Question</label>
                                                <input
                                                    style={{ marginRight: '5px' }}
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="trial"
                                                    checked={trial}
                                                    value={trial}
                                                    onChange={() => setTrial((prev) => !prev)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="status">Status </label>
                                                <input
                                                    style={{ marginRight: '5px' }}
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="status"
                                                    checked={status}
                                                    value={status}
                                                    onChange={() => setStatus((prev) => !prev)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="box-footer">
                                        <button type="submit" className="btn btn-primary">
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </section>
                    </div>
                </Fragment>
            )}
        </Fragment>
    );
};

export default EditQuestion;
